/**
 * Displays a list of orders that the user has access to.
 */
import { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import type { ColumnDef } from '@tanstack/react-table';
import { OrderListItem, OrderRepository } from 'src/api/order_repository';
import StarryCardIcon from 'src/assets/starry_card.svg';
import {
  SquareCheckedIcon,
  SquareUncheckedIcon,
} from 'src/components/multi_select_checkbox_group';
import { PageTitle } from 'src/components/page_title';
import { DEFAULT_CURRENT_PAGE } from 'src/components/pagination/use_pagination';
import { SearchInput } from 'src/components/search_input';
import { SpecimenTypeFilter } from 'src/components/specimen_type_filter';
import { StatusFilter } from 'src/components/status_filter';
import { DataTable } from 'src/components/table';
import {
  ALL_ORDERS_FILTER,
  ALL_SPECIMEN_TYPE_FILTER,
} from 'src/constants/key_constants';
import { useOrders } from 'src/distribution_platforms/features/hooks/use_orders';
import {
  useWorkflow,
  useWorkflowType,
} from 'src/distribution_platforms/features/hooks/use_workflow';
import {
  getLabelFromFilter,
  isAllOrdersFilter,
  parseStatusFilterValue,
  parseSpecimenTypeFilterValues,
  isAllSpecimenTypeFilter,
  getStatusFilterOptions,
} from 'src/distribution_platforms/features/order_list';
import {
  getDefaultOrdersSortBy,
  getOrdersQueryParams,
} from 'src/distribution_platforms/features/utils';
import {
  useIsClinicAdmin,
  useIsOrderingPhysician,
  useUser,
} from 'src/hooks/use_auth';
import { useDebounce } from 'src/hooks/use_debounce';
import { useEffectiveSearchParams } from 'src/hooks/use_effective_search_params';
import { useOrderingSpecimenTypes } from 'src/hooks/use_ordering_specimen_types';
import { Seq, SortBy } from 'src/types';
import { WorkflowType } from 'src/types/workflows';

const OrderListContainer = styled(Box)(({ theme }) => ({
  alignSelf: 'center',
  display: 'flex',
  flexDirection: 'column',
  height: 'min(100%, 1000px)',
  maxWidth: '2000px',
  width: '100%',
  position: 'relative',
  backgroundColor: theme.palette.grey[100],
}));

const OrderListHeader = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '2000px',
  width: '100%',
});

const TitleAndSearchRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  minHeight: '32px', // Changed from fixed height to min-height
  marginBottom: '16px',
  gap: '40px',
  flexWrap: 'wrap', // Allow wrapping on smaller screens
  [theme.breakpoints.down('lg')]: {
    gap: '20px',
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '16px',
  },
}));

const TitleAndSearchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '40px',
  flexWrap: 'wrap',
  flex: 1,
  minWidth: 0, // Allow shrinking
  [theme.breakpoints.down('lg')]: {
    gap: '10px',
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '12px',
    width: '100%',
  },
}));

export type OrderListPageLayoutProps = {
  orderRepository: OrderRepository;
  backgroundSearchParams?: URLSearchParams;
  isBackgroundMode?: boolean;
};

const OrderListPageLayout: React.FC<OrderListPageLayoutProps> = ({
  orderRepository,
  backgroundSearchParams,
  isBackgroundMode = false,
}) => {
  const theme = useTheme();
  const { organization } = useParams();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const effectiveSearchParams = useEffectiveSearchParams();
  const userInfo = useUser();
  const isOrderingPhysician = useIsOrderingPhysician();
  const isClinicAdmin = useIsClinicAdmin();

  const { orders, totalOrders } = useOrders(
    orderRepository,
    effectiveSearchParams,
    !isBackgroundMode, // Pass useLoader=true for normal mode, false for background mode
  );

  // Initialize search input value with the current search text from URL.
  // This ensures that the search text is reflected after refreshing the page.
  const searchText = effectiveSearchParams.get('searchText') || '';
  const [searchInputValue, setSearchInputValue] = useState(searchText);

  // Get status filter from URL params, default to ALL_ORDERS_FILTER
  const statusFilter = parseStatusFilterValue(
    effectiveSearchParams.get('statusFilter'),
  );

  // Get specimen type filter from URL params, default to empty array (all types)
  const specimenTypeFilters = parseSpecimenTypeFilterValues(
    effectiveSearchParams.get('specimenTypeFilter'),
  );

  // Needs Attention filter from URL params, default unchecked
  const needsAttentionFirst =
    effectiveSearchParams.get('needsAttentionFirst') === 'true';

  useEffect(() => {
    // Only set default params if we're not using background search params
    // and the current search params are empty
    // AND we're not in background mode (which would interfere with modal routes)
    if (!backgroundSearchParams && !isBackgroundMode) {
      if (effectiveSearchParams.size === 0) {
        setSearchParams(
          {
            sortBy: getDefaultOrdersSortBy(),
            seq: Seq.Desc,
            page: DEFAULT_CURRENT_PAGE.toString(),
          },
          { replace: true },
        );
      } else {
        const currentPage = parseInt(
          effectiveSearchParams.get('page') || `${DEFAULT_CURRENT_PAGE}`,
        );

        // If the page query parameter is invalid, set it to the default value.
        if (isNaN(currentPage)) {
          setSearchParams(
            {
              ...Object.fromEntries(effectiveSearchParams.entries()),
              sortBy: getDefaultOrdersSortBy(),
              seq: Seq.Desc,
              page: DEFAULT_CURRENT_PAGE.toString(),
            },
            { replace: true },
          );
        }
      }
    }
  }, [
    backgroundSearchParams,
    effectiveSearchParams,
    setSearchParams,
    isBackgroundMode,
  ]);

  const { hasMultipleSpecimenTypes } = useOrderingSpecimenTypes();

  const workflow = useWorkflow();
  const workflowType = useWorkflowType();
  const showNeedsAttentionFilter =
    workflowType === WorkflowType.CLINIC &&
    (isOrderingPhysician || isClinicAdmin);
  const columns = useMemo(
    () => workflow.getOrderListColumns(workflow),
    [workflow],
  );
  const statusFilterOptions = useMemo(() => {
    return getStatusFilterOptions(workflowType);
  }, [workflowType]);

  const queryParams = getOrdersQueryParams(effectiveSearchParams, workflowType);
  const needsAttentionQueryParam =
    'needsAttentionFirst' in queryParams &&
    queryParams.needsAttentionFirst === true;

  const isFilterApplied = useMemo(() => {
    return (
      !!queryParams.searchText ||
      !!queryParams.specimenTypes?.length ||
      !!queryParams.statuses ||
      needsAttentionQueryParam
    );
  }, [
    queryParams.searchText,
    queryParams.specimenTypes,
    queryParams.statuses,
    needsAttentionQueryParam,
  ]);

  const debouncedUpdateSearchParams = useDebounce((searchText: string) => {
    updateSearchParams({ searchText });
  }, 300);

  const updateSearchParams = (
    updates: Partial<{
      page: number;
      sortBy: SortBy;
      seq: Seq;
      searchText: string;
      statusFilter: string;
      specimenTypeFilter: string[];
      needsAttentionFirst: boolean;
    }>,
  ) => {
    // Get current params as an object
    const currentSearchParams = Object.fromEntries(
      effectiveSearchParams.entries(),
    );

    // Reset to page 1 if any non-page filter changes
    if (Object.keys(updates).some((key) => key !== 'page')) {
      updates.page = 1;
    }

    const newSearchParams: Record<string, string> = { ...currentSearchParams };
    // Convert each update to string and handle special cases
    Object.entries(updates).forEach(([key, value]) => {
      if (!value) {
        delete newSearchParams[key]; // Remove param if null/undefined/empty/false
      } else if (key === 'statusFilter' && isAllOrdersFilter(value as string)) {
        // Don't include the status filter in URL if it's "all orders"
        delete newSearchParams[key];
      } else if (
        key === 'specimenTypeFilter' &&
        isAllSpecimenTypeFilter(value as string[])
      ) {
        // Don't include the specimen type filter in URL if it's "all types"
        delete newSearchParams[key];
      } else {
        newSearchParams[key] = String(value);
      }
    });

    setSearchParams(newSearchParams);
  };

  const handlePageChange = (newPage: number) => {
    updateSearchParams({ page: newPage });
  };

  const handleSort = (sortBy: SortBy) => {
    let sortDirection: Seq = queryParams.seq!;

    if (queryParams.sortBy === sortBy) {
      sortDirection = queryParams.seq === Seq.Asc ? Seq.Desc : Seq.Asc;
    }

    updateSearchParams({ sortBy, seq: sortDirection });
  };

  // Row link for middle-click / open in new tab support
  const getRowLink = (order: OrderListItem) => {
    return workflow.getRowLink(order, organization!, location, userInfo.id);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchText = event.target.value;
    setSearchInputValue(searchText);
    const sanitizedSearchText = searchText.replace(/[^a-zA-Z0-9.,@()\- ]/g, '');
    debouncedUpdateSearchParams(sanitizedSearchText);
  };

  const handleStatusFilterChange = (statusFilter: string) => {
    updateSearchParams({ statusFilter });
  };
  const handleSpecimenTypeFilterChange = (specimenTypeFilter: string[]) => {
    updateSearchParams({ specimenTypeFilter });
  };
  const handleNeedsAttentionChange = (checked: boolean) => {
    updateSearchParams({ needsAttentionFirst: checked });
  };
  const hasMultipleStatusFilters =
    Array.from(statusFilterOptions.keys()).filter(
      (key) => key !== ALL_ORDERS_FILTER,
    ).length > 0;

  return (
    <OrderListContainer>
      <OrderListHeader>
        <TitleAndSearchRow>
          <TitleAndSearchContainer>
            <PageTitle
              title={
                needsAttentionFirst
                  ? 'Needs Attention'
                  : getLabelFromFilter(statusFilter, workflowType)
              }
            />
            <SearchInput
              {...workflow.orderListSearchConfig}
              value={searchInputValue}
              onChange={handleSearch}
            />
          </TitleAndSearchContainer>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignSelf: { xs: 'flex-start', md: 'flex-end' },
            }}
          >
            {showNeedsAttentionFilter && (
              <FormControlLabel
                control={
                  <Checkbox
                    icon={<SquareUncheckedIcon />}
                    checkedIcon={<SquareCheckedIcon />}
                    checked={needsAttentionFirst}
                    onChange={(e) =>
                      handleNeedsAttentionChange(e.target.checked)
                    }
                  />
                }
                label='Needs Attention'
                data-testid='needs-attention-filter'
                sx={{
                  margin: 0,
                  padding: '4px',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                  '& .MuiFormControlLabel-label': {
                    flex: 1,
                  },
                }}
              />
            )}
            {hasMultipleSpecimenTypes && (
              <SpecimenTypeFilter
                values={
                  specimenTypeFilters.length
                    ? specimenTypeFilters
                    : [ALL_SPECIMEN_TYPE_FILTER]
                }
                onChange={handleSpecimenTypeFilterChange}
              />
            )}
            {hasMultipleStatusFilters && (
              <StatusFilter
                value={statusFilter}
                onChange={handleStatusFilterChange}
                statusFilterOptions={statusFilterOptions}
              />
            )}
          </Box>
        </TitleAndSearchRow>
        {showNeedsAttentionFilter && (
          <Box
            sx={{
              backgroundColor: theme.palette.primary.light,
              padding: 1,
              borderRadius: 2,
              width: 'fit-content',
            }}
          >
            <Typography
              variant='subtitle2'
              color={theme.palette.text.primary}
              sx={{ fontWeight: 600 }}
            >
              Orders marked with{' '}
              <WarningIcon
                sx={{
                  color: 'warning.main',
                  fontSize: 18,
                  verticalAlign: 'text-bottom',
                  mx: 0.25,
                }}
                aria-label='needs-attention'
              />{' '}
              need additional information or documents. Please provide the
              missing items to help avoid processing delays.
            </Typography>
          </Box>
        )}
      </OrderListHeader>
      {totalOrders === 0 && !isFilterApplied && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Box
            sx={{
              mb: 5,
            }}
          >
            <img
              src={StarryCardIcon}
              alt='Starry Card'
              style={{ maxWidth: '100%' }}
            />
          </Box>
          <Typography
            variant='h6'
            sx={{
              color: theme.palette.text.primary,
              textAlign: 'center',
            }}
          >
            There are no orders yet
          </Typography>
        </Box>
      )}
      {totalOrders === 0 && isFilterApplied && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Box
            sx={{
              mb: 5,
            }}
          >
            <img
              src={StarryCardIcon}
              alt='Starry Card'
              style={{ maxWidth: '100%' }}
            />
          </Box>
          <Typography
            variant='h6'
            color={theme.palette.text.primary}
            sx={{
              textAlign: 'center',
            }}
          >
            No results matched the input criteria
          </Typography>
          <Typography
            variant='body2'
            color={theme.palette.text.secondary}
            sx={{
              textAlign: 'center',
            }}
          >
            Please check your input and try again.
          </Typography>
        </Box>
      )}
      {totalOrders > 0 && (
        <DataTable<OrderListItem, string>
          data={orders}
          // Platform list columns use platform-specific row fragments.
          columns={columns as ColumnDef<OrderListItem, string>[]}
          onSort={(sortBy) => handleSort(sortBy as SortBy)}
          getRowLink={getRowLink}
          paginationProps={{
            pageSize: queryParams.perPage,
            currentPage: queryParams.page,
            itemCount: totalOrders,
            onPageSelected: handlePageChange,
          }}
          ariaLabel='orders table'
        />
      )}
    </OrderListContainer>
  );
};

export const OrderListPage = ({
  orderRepository,
  backgroundSearchParams,
  isBackgroundMode,
}: {
  orderRepository: OrderRepository;
  backgroundSearchParams?: URLSearchParams;
  isBackgroundMode?: boolean;
}) => {
  return (
    <OrderListPageLayout
      orderRepository={orderRepository}
      backgroundSearchParams={backgroundSearchParams}
      isBackgroundMode={isBackgroundMode}
    />
  );
};
